import React from "react";
import { Phone, Menu as MenuIcon, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useSection } from "@/context/SiteContext";
import { telHref, resolveImageUrl } from "@/lib/site";
import api from "@/lib/api";

export default function Header() {
  const [open, setOpen] = React.useState(false);
  const [hasEvents, setHasEvents] = React.useState(false);
  const brand = useSection("brand");
  const nav = useSection("nav");
  const contact = useSection("contact");

  React.useEffect(() => {
    api.get("/events")
      .then(({ data }) => setHasEvents(Array.isArray(data) && data.length > 0))
      .catch(() => setHasEvents(false));
  }, []);

  const phone = contact.phone || "0660-511 30";
  const tel = telHref(contact.phone_tel, phone);
  const logoSrc = resolveImageUrl(brand.logo_url);
  const brandName = brand.name || "Mon Ami";

  const items = [
    { label: "Lunch", href: "#lunch" },
    ...(hasEvents ? [{ label: "Händelser", href: "#handelser" }] : []),
    { label: nav.menu_label || "Meny", href: "#meny" },
    { label: nav.about_label || "Om oss", href: "#om" },
    { label: nav.reviews_label || "Recensioner", href: "#recensioner" },
    { label: nav.contact_label || "Kontakt", href: "#kontakt" },
  ];

  return (
    <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[var(--border)]/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="brand-home-link" className="flex items-center gap-2">
          {logoSrc ? (
            <img
              src={logoSrc}
              alt={brandName}
              className="h-11 w-11 sm:h-12 sm:w-12 rounded-full object-cover ring-1 ring-[var(--border)]"
            />
          ) : (
            <>
              <span className="inline-block w-8 h-8 rounded-full bg-[var(--primary)] text-white grid place-items-center font-bold font-serif-display text-lg">
                {(brand.initial || "M").slice(0, 1)}
              </span>
              <span className="font-serif-display text-xl font-bold tracking-tight">{brandName}</span>
            </>
          )}
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {items.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.href.replace("#", "")}-link`}
              className="text-sm font-medium text-[var(--text-muted)] hover:text-[var(--primary)] transition-colors"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={tel}
            data-testid="header-call-button"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white px-4 py-2 text-sm font-semibold transition-colors"
          >
            <Phone size={15} strokeWidth={2.5} />
            {phone}
          </a>
          <button
            aria-label="Öppna meny"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="md:hidden w-10 h-10 grid place-items-center rounded-full border border-[var(--border)]"
          >
            {open ? <X size={18} /> : <MenuIcon size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-[var(--border)] bg-white">
          <div className="px-4 py-3 flex flex-col gap-1">
            {items.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                className="py-3 px-2 text-base font-medium text-[var(--text)] rounded-lg hover:bg-[var(--surface-alt)]"
              >
                {n.label}
              </a>
            ))}
            <a
              href={tel}
              data-testid="mobile-menu-call"
              className="mt-2 inline-flex items-center gap-2 rounded-full bg-[var(--primary)] text-white px-4 py-3 font-semibold"
            >
              <Phone size={15} /> Ring {phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
